import { Moment } from 'moment';
import React from 'react';
import _ from 'underscore';

import classNames from 'classnames';
import { StickyBoxWaitForMount } from 'app/common/components/sticky-box-wait-for-mount';
import { DismissAnimation, TransitionGroup } from './dismiss-animation';

import { ScrolledPastWatcher } from 'app/common/components/scrolled-past-watcher';
import { SectionHeader } from './section-header';
import { TeamCardDueScrollNavigation } from './team-card-due-scroll-navigation';
import { Empty, EmptyToday } from './team-cards-due-empty';
import {
  DueDateCardModel,
  getGroups,
  GroupName,
} from './util/team-cards-due-groups';

import styles from './team-cards-due-section.less';

interface TeamCardsDueListProps {
  listName: string;
  groupName: GroupName;
  dueDateCards: DueDateCardModel[];
  renderDueDate: (dueDateCard: DueDateCardModel) => JSX.Element;
  EmptyComponent?: typeof Empty | typeof EmptyToday;
  setGroupRef: (groupName: GroupName, groupRef: HTMLDivElement) => void;
  setHighlightedTab: (didScrollPast: boolean) => void;
}

class TeamCardsDueList extends React.Component<TeamCardsDueListProps> {
  shouldComponentUpdate(nextProps: TeamCardsDueListProps) {
    return !(
      this.props.groupName === nextProps.groupName &&
      this.props.listName === nextProps.listName &&
      this.props.renderDueDate === nextProps.renderDueDate &&
      _.isEqual(
        this.props.dueDateCards.map((cardId: DueDateCardModel) => cardId),
        nextProps.dueDateCards.map((cardId: DueDateCardModel) => cardId),
      )
    );
  }

  render() {
    const {
      dueDateCards,
      groupName,
      listName,
      renderDueDate,
      EmptyComponent,
      setGroupRef,
      setHighlightedTab,
    } = this.props;

    return (
      <div ref={setGroupRef.bind(null, groupName)}>
        <ScrolledPastWatcher setScrolledPast={setHighlightedTab}>
          <SectionHeader>{listName}</SectionHeader>
          {dueDateCards.length === 0 && EmptyComponent && <EmptyComponent />}
          <TransitionGroup component="ul" className={styles.dueList}>
            {dueDateCards.map((dueDateCard) => (
              <DismissAnimation
                id={dueDateCard.cardId}
                key={dueDateCard.cardId}
              >
                <li key={dueDateCard.cardId} className={styles.dueDateCard}>
                  {renderDueDate(dueDateCard)}
                </li>
              </DismissAnimation>
            ))}
          </TransitionGroup>
        </ScrolledPastWatcher>
      </div>
    );
  }
}

interface TeamCardsDueSectionProps
  extends React.HTMLAttributes<HTMLDivElement> {
  dueDateCards: DueDateCardModel[];
  getDueDateOf: (dueDateCard: DueDateCardModel) => number;
  renderDueDate: (dueDateCard: DueDateCardModel) => JSX.Element;
  onClickGroup: React.EventHandler<React.MouseEvent<HTMLElement>>;
  selectedGroup: GroupName;
  setGroupRef: (groupName: GroupName, groupRef: HTMLDivElement) => void;
  setScrolledPastTab: (groupName: string) => void;
  now: Moment;
  isBoardsMenuPinned: boolean;
}

export const TeamCardsDueSection: React.FunctionComponent<
  TeamCardsDueSectionProps
> = ({
  children,
  dueDateCards,
  renderDueDate,
  getDueDateOf,
  now,
  onClickGroup,
  selectedGroup: selectedGroupProp,
  setGroupRef,
  setScrolledPastTab,
  isBoardsMenuPinned,
  ...props
}) => {
  const groups = getGroups(dueDateCards, getDueDateOf, now);

  let selectedGroup = selectedGroupProp;
  if (!groups.some(({ group }) => group === selectedGroupProp)) {
    selectedGroup = groups[0].group;
  }

  const calcHighlightedTab = (groupName: string, didScrollPast: boolean) => {
    if (!didScrollPast) {
      setScrolledPastTab(groupName);
    } else {
      switch (groupName) {
        case 'overdue':
          setScrolledPastTab('due-today');
          break;
        case 'due-today':
          setScrolledPastTab('due-tomorrow');
          break;
        case 'due-tomorrow':
          setScrolledPastTab('due-week');
          break;
        case 'due-week':
          setScrolledPastTab('due-later');
          break;
        default:
          setScrolledPastTab('overdue');
      }
    }
  };

  return (
    <div className={styles.dueDateSection} {...props}>
      <div className={styles.dueDateLists}>
        {groups.map(({ group, name, cards }) => (
          <TeamCardsDueList
            key={group}
            dueDateCards={cards}
            renderDueDate={renderDueDate}
            listName={name}
            groupName={group}
            setGroupRef={setGroupRef}
            setHighlightedTab={calcHighlightedTab.bind(null, group)}
            EmptyComponent={group === 'due-today' ? EmptyToday : Empty}
          />
        ))}
      </div>

      <StickyBoxWaitForMount
        className={classNames(styles.scrollNavContainer, {
          [styles.pinnedBoardsMenu]: isBoardsMenuPinned,
        })}
      >
        <TeamCardDueScrollNavigation
          tabs={groups.map(({ group, name, cards }) => ({
            count: cards.length,
            tabText: name,
            selected: selectedGroup === group,
            onClick: onClickGroup.bind(null, group),
          }))}
        />
      </StickyBoxWaitForMount>
      {children}
    </div>
  );
};
